import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Container,
  Button,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  styled,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { 
  ContentCut as ScissorsIcon,
  Store as StoreIcon,
  EventAvailable as BookingIcon,
  Dashboard as DashboardIcon,
  Person as ProfileIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

const GlassNavbar = styled(AppBar)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.15)',
  backdropFilter: 'blur(20px)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  display: 'none',
  [theme.breakpoints.up('sm')]: {
    display: 'block',
  },
}));

const NavButton = styled(Button)<{ component?: React.ElementType; to?: string }>(({ theme }) => ({
  color: theme.palette.text.primary,
  textTransform: 'none',
  fontSize: '1rem',
  padding: '6px 16px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
}));

const LogoButton = styled(Button)<{ component?: React.ElementType; to?: string }>(({ theme }) => ({
  color: theme.palette.text.primary,
  textTransform: 'none',
  fontSize: '1.2rem',
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '&:hover': {
    backgroundColor: 'transparent',
    '& .MuiSvgIcon-root': {
      transform: 'rotate(180deg)',
    },
  },
  '& .MuiSvgIcon-root': {
    fontSize: '1.5rem',
    transition: 'transform 0.3s ease-in-out',
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiBackdrop-root': {
    backgroundColor: 'transparent',
  },
  '& .MuiPaper-root': {
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(20px)',
    borderRadius: 8,
    border: '1px solid rgba(255, 255, 255, 0.3)',
    marginTop: theme.spacing(1),
  },
}));

const Navbar = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isVerySmallScreen = useMediaQuery('(max-width:390px)');

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setProfileAnchor(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/');
  };

  return (
    <GlassNavbar position="sticky">
      <Container maxWidth="lg">
        <Toolbar sx={{ justifyContent: 'space-between', px: 0 }}>
          {/* Logo */}
          <LogoButton
            component={RouterLink}
            to="/"
          >
            {t('app.name')}
          </LogoButton>

          {/* Navigation Links */}
          <Stack direction="row" spacing={isVerySmallScreen ? 0.5 : 1}>
            <NavButton
              component={RouterLink}
              to="/"
              startIcon={<BookingIcon />}
              sx={{ 
                display: { xs: isVerySmallScreen ? 'none' : 'flex', sm: 'flex' } 
              }}
            >
              {t('nav.book')}
            </NavButton>

            <NavButton
              component={RouterLink}
              to="/barber-shops"
              startIcon={<StoreIcon />}
              sx={{ 
                display: { xs: isVerySmallScreen ? 'none' : 'flex', sm: 'flex' } 
              }}
            >
              {t('nav.shop')}
            </NavButton>

            {user?.type === 'barber' && (
              <NavButton
                component={RouterLink}
                to="/barber"
                startIcon={<DashboardIcon />}
                sx={{ 
                  display: { xs: isVerySmallScreen ? 'none' : 'flex', sm: 'flex' } 
                }}
              >
                {t('nav.dashboard')}
              </NavButton>
            )}

            {user ? (
              <NavButton
                onClick={handleProfileClick}
                startIcon={<ProfileIcon />}
                sx={{ 
                  display: { xs: isVerySmallScreen ? 'none' : 'flex', sm: 'flex' } 
                }}
              >
                {t('nav.profile')}
              </NavButton>
            ) : (
              <NavButton
                component={RouterLink}
                to="/login"
                startIcon={<ProfileIcon />}
                sx={{ 
                  display: { xs: isVerySmallScreen ? 'none' : 'flex', sm: 'flex' } 
                }}
              >
                {t('nav.signin')}
              </NavButton>
            )}
          </Stack>

          {/* Profile Menu */}
          <StyledMenu
            id="profile-menu"
            anchorEl={profileAnchor}
            open={Boolean(profileAnchor)}
            onClose={handleMenuClose}
          >
            <MenuItem 
              component={RouterLink} 
              to={user?.type === 'barber' ? '/barber/profile' : '/profile'}
              onClick={handleMenuClose}
            >
              <ListItemIcon>
                <ProfileIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('nav.profile')}</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('nav.signout')}</ListItemText>
            </MenuItem>
          </StyledMenu>
        </Toolbar>
      </Container>
    </GlassNavbar>
  );
};

export default Navbar; 